/* eslint-disable @typescript-eslint/naming-convention */
import { PromiseBox } from './PromiseBox';
import { Value } from './Value';

const TIMEOUT = 10000;

interface ResultLoading {
    readonly type: 'loading';
    readonly whenReady: Promise<void>;
}

interface ResultReady<T> {
    readonly type: 'ready';
    readonly value: T;
};

interface ResultError {
    readonly type: 'error';
    readonly message: string;
}

export type Result<T> = ResultLoading | ResultReady<T> | ResultError;

const send = <T>(loadValue: () => Promise<T>, customTimeout?: number): Promise<Result<T>> => {
    return new Promise((resolve) => {
        const timeout = customTimeout ?? TIMEOUT;

        setTimeout(() => {
            resolve({
                type: 'error',
                message: `Timeout ${timeout}ms`,
            });
        }, timeout);

        loadValue().then((loadedValue) => {
            resolve({
                type: 'ready',
                value: loadedValue,
            });
        }).catch((err) => {
            console.error(err);
            const message = String(err);

            resolve({
                type: 'error',
                message: message
            });
        });
    });
};

class Request<T> {
    private isInit: boolean = false;
    public readonly whenReady: PromiseBox<void>;
    private readonly value: Value<Result<T>>;
    private readonly customTimeout: number | undefined;

    public constructor(
        private readonly getValue: () => Promise<T>,
        private readonly prevValue: Result<T> | null,
        customTimeout?: number
    ) {
        this.whenReady = new PromiseBox<void>();

        this.value = new Value({
            type: 'loading',
            whenReady: this.whenReady.promise,
        });
        this.customTimeout = customTimeout;
    }

    public init(): void {
        if (this.isInit) {
            return;
        }

        this.isInit = true;

        const valuePromise = send(this.getValue, this.customTimeout);

        setTimeout(async () => {
            const value = await valuePromise;
            this.value.setValue(value);
            this.whenReady.resolve();
        }, 0);
    }

    public get(): Result<T> {
        const current = this.value.getValue();

        if (current.type !== 'loading') {
            return current;
        }

        if (this.prevValue !== null) {
            return this.prevValue;
        }

        return current;
    }
}

export class Resource<T> {
    private request: Value<Request<T>>;
    private readonly customTimeout: number | undefined;

    public constructor(
        private readonly loadValue: () => Promise<T>,
        customTimeout?: number
    ) {
        this.request = new Value(new Request(this.loadValue, null, customTimeout));
        this.customTimeout = customTimeout;
    }

    public get(): Result<T> {
        const request = this.request.getValue();
        request.init();
        return request.get();
    }

    public getReady(): T | null {
        const result = this.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public async refresh(): Promise<void> {
        const prevValue = this.get();
        const request = new Request(this.loadValue, prevValue, this.customTimeout);
        request.init();

        this.request.setValue(request);
        await request.whenReady.promise;
    }

    public clear(): void {
        this.request.setValue(new Request(this.loadValue, null));
    }
}
